import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Building2, Loader2, Search, X, CheckSquare, Square, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * RouteMunicipalitiesModal
 *
 * Admin-only. Permite associar uma rota a 1..N municípios. A associação é
 * usada pelo dashboard `/municipio/dashboard` — só as rotas ligadas ao
 * município do utilizador aparecem lá.
 *
 * UI: search por nome + lista com checkboxes agrupada por distrito.
 * Mostra chips dos selecionados no topo para remoção rápida.
 *
 * Props:
 *   route       — objecto route (usa route_id + name)
 *   open        — bool
 *   onClose     — () => void
 *   onSaved?    — (municipality_ids: string[]) => void
 */
const RouteMunicipalitiesModal = ({ route, open, onClose, onSaved }) => {
  const [municipalities, setMunicipalities] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');
  // Anchor para shift+click ranged select. Guarda o municipality_id do
  // último item clicado — próximo shift+click seleciona/desselecciona tudo
  // entre esse anchor e o item actual (na ordem flat visível).
  const lastClickIdRef = useRef(null);

  useEffect(() => {
    if (!open || !route?.route_id) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [muniRes, linkRes] = await Promise.all([
          axios.get(`${API}/municipalities`),
          axios.get(`${API}/admin/routes/${route.route_id}/municipalities`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        setMunicipalities(muniRes.data || []);
        setSelected(new Set(linkRes.data?.municipality_ids || []));
      } catch (e) {
        toast.error('Erro a carregar municípios');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, route?.route_id]);

  const toggle = (mid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(mid)) next.delete(mid); else next.add(mid);
      return next;
    });
  };

  // Lista visível actual (na ordem em que é renderizada). Usada por:
  //   - shift+click para range selection
  //   - "Selecionar visíveis" para bulk toggle
  const filteredFlat = useMemo(() => {
    const text = q.trim().toLowerCase();
    return text
      ? municipalities.filter((m) =>
          m.name.toLowerCase().includes(text) ||
          (m.district || '').toLowerCase().includes(text))
      : municipalities;
  }, [municipalities, q]);

  // Handler avançado — Ctrl/Cmd+click, Shift+click, click normal.
  //   • plain click  → toggle 1 item
  //   • Ctrl/Cmd+click → toggle 1 item (mantém anchor para range)
  //   • Shift+click   → seleciona range entre anchor e este item (na ordem flat)
  const handleRowClick = (mid, e) => {
    const isShift = e?.shiftKey;
    if (isShift && lastClickIdRef.current) {
      const anchor = lastClickIdRef.current;
      const ids = filteredFlat.map((m) => m.municipality_id);
      const iA = ids.indexOf(anchor);
      const iB = ids.indexOf(mid);
      if (iA >= 0 && iB >= 0) {
        const [lo, hi] = iA < iB ? [iA, iB] : [iB, iA];
        const range = ids.slice(lo, hi + 1);
        // Decide se estamos a adicionar ou a remover: se o item alvo NÃO
        // está selecionado, ADICIONA todos; caso contrário REMOVE todos.
        const shouldSelect = !selected.has(mid);
        setSelected((prev) => {
          const next = new Set(prev);
          range.forEach((id) => (shouldSelect ? next.add(id) : next.delete(id)));
          return next;
        });
        lastClickIdRef.current = mid;
        return;
      }
    }
    toggle(mid);
    lastClickIdRef.current = mid;
  };

  const selectAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      filteredFlat.forEach((m) => next.add(m.municipality_id));
      return next;
    });
  };
  const clearAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      filteredFlat.forEach((m) => next.delete(m.municipality_id));
      return next;
    });
  };
  const selectDistrict = (district) => {
    setSelected((prev) => {
      const next = new Set(prev);
      municipalities
        .filter((m) => (m.district || 'Outros') === district)
        .forEach((m) => next.add(m.municipality_id));
      return next;
    });
  };
  const clearDistrict = (district) => {
    setSelected((prev) => {
      const next = new Set(prev);
      municipalities
        .filter((m) => (m.district || 'Outros') === district)
        .forEach((m) => next.delete(m.municipality_id));
      return next;
    });
  };
  const clearAll = () => {
    setSelected(new Set());
    lastClickIdRef.current = null;
  };

  const grouped = useMemo(() => {
    const g = {};
    filteredFlat.forEach((m) => {
      const d = m.district || 'Outros';
      if (!g[d]) g[d] = [];
      g[d].push(m);
    });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredFlat]);

  const save = async () => {
    setSaving(true);
    try {
      const ids = Array.from(selected);
      const { data } = await axios.put(
        `${API}/admin/routes/${route.route_id}/municipalities`,
        { municipality_ids: ids },
        { withCredentials: true },
      );
      toast.success(`Associado a ${ids.length} município(s)`);
      onSaved?.(data.municipality_ids || ids);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  const nameOf = (mid) => municipalities.find((m) => m.municipality_id === mid)?.name || mid;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="route-muni-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-indigo-700" /> Municípios da rota
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium">{route?.name}</span> — selecciona os concelhos por onde a rota passa. As câmaras municipais vão poder ver esta rota no seu dashboard.
          </DialogDescription>
        </DialogHeader>

        {/* Selected chips */}
        {selected.size > 0 && (
          <div className="flex flex-wrap gap-1.5 py-2 border-b border-slate-200">
            {Array.from(selected).map((mid) => (
              <span key={mid} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-indigo-100 text-indigo-800 text-xs font-medium">
                {nameOf(mid)}
                <button type="button" className="ml-0.5 rounded-full hover:bg-indigo-200 p-0.5" onClick={() => toggle(mid)}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Search + bulk toolbar */}
        <div className="flex flex-col gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Procurar concelho ou distrito…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-8"
              data-testid="muni-search"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <button
              type="button"
              onClick={selectAllVisible}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-emerald-400 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 font-semibold"
              data-testid="muni-select-visible"
            >
              <CheckSquare className="w-3 h-3" /> Selecionar visíveis ({filteredFlat.length})
            </button>
            <button
              type="button"
              onClick={clearAllVisible}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 font-semibold"
              data-testid="muni-clear-visible"
            >
              <Square className="w-3 h-3" /> Limpar visíveis
            </button>
            <button
              type="button"
              onClick={clearAll}
              disabled={selected.size === 0}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-rose-300 text-rose-600 hover:bg-rose-50 font-semibold disabled:opacity-40"
              data-testid="muni-clear-all"
            >
              <X className="w-3 h-3" /> Limpar tudo
            </button>
            <span className="ml-auto flex items-center gap-1 text-slate-500 italic">
              <Sparkles className="w-3 h-3" /> Dica: <strong>Shift+click</strong> para intervalo, <strong>Ctrl+click</strong> para adicionar.
            </span>
          </div>
        </div>

        {/* List grouped by district */}
        <div className="flex-1 min-h-0 overflow-y-auto pr-2 mt-1 space-y-3" data-testid="muni-list">
          {loading && (
            <div className="text-center py-8 text-sm text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> A carregar municípios…
            </div>
          )}
          {!loading && grouped.length === 0 && (
            <div className="text-center py-8 text-sm text-slate-400">Sem resultados</div>
          )}
          {!loading && grouped.map(([district, list]) => {
            const allInDistrict = list.every((m) => selected.has(m.municipality_id));
            return (
              <div key={district}>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="text-xs font-bold uppercase text-slate-500 flex-1">{district}</div>
                  <button
                    type="button"
                    onClick={() => (allInDistrict ? clearDistrict(district) : selectDistrict(district))}
                    className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-slate-300 text-slate-600 hover:bg-slate-100"
                    data-testid={`muni-district-toggle-${district}`}
                  >
                    {allInDistrict ? 'Limpar distrito' : `Selecionar distrito (${list.length})`}
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {list.map((m) => {
                    const isOn = selected.has(m.municipality_id);
                    return (
                      <div
                        key={m.municipality_id}
                        onClick={(e) => handleRowClick(m.municipality_id, e)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleRowClick(m.municipality_id, e);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-pressed={isOn}
                        className={`flex items-center gap-2 p-2 rounded-md border cursor-pointer text-sm select-none ${isOn ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'}`}
                        data-testid={`muni-item-${m.municipality_id}`}
                      >
                        <Checkbox
                          checked={isOn}
                          onCheckedChange={() => toggle(m.municipality_id)}
                          // Impede que o click no checkbox faça bubbling para o div — se ambos correrem, cancelam-se.
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="truncate">{m.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-slate-200">
          <span className="text-xs text-slate-500">{selected.size} selecionado(s)</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="muni-cancel">Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-indigo-700 hover:bg-indigo-800" data-testid="muni-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Guardar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RouteMunicipalitiesModal;
